/**
 * Las tareas de una ficha, para empotrar en el detalle de un lead, un
 * propietario o una petición.
 *
 * Existe porque una lista de tareas en su propia pantalla es una lista que hay
 * que acordarse de mirar. Aquí aparecen donde se está trabajando: quien abre un
 * lead ve que hay que llamarle mañana sin ir a buscarlo.
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus, CheckSquare } from 'lucide-react';
import { api } from '@/lib/api';
import type { Task, TaskRelacion } from '@/lib/types';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import FilaTarea from './FilaTarea';
import TareaModal, { type DatosTarea } from './TareaModal';

interface Props {
  relatedType: TaskRelacion;
  relatedId: string;
  /** Nombre de la ficha, para que la tarea lo lleve congelado. */
  relatedName?: string | null;
  titulo?: string;
}

export default function BloqueTareas({ relatedType, relatedId, relatedName, titulo = 'Tareas' }: Props) {
  const [tareas, setTareas] = useState<Task[]>([]);
  const [cargando, setCargando] = useState(true);
  const [verCerradas, setVerCerradas] = useState(false);
  const [modal, setModal] = useState<{ tarea: Task | null } | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const datos = await api.getTasks({ relatedType, relatedId, incluirCerradas: 'true' });
      setTareas(datos);
    } catch {
      setTareas([]);
    } finally {
      setCargando(false);
    }
  }, [relatedType, relatedId]);

  useEffect(() => { cargar(); }, [cargar]);

  const abiertas = tareas.filter(t => t.status === 'pending');
  const cerradas = tareas.filter(t => t.status !== 'pending');
  const visibles = verCerradas ? tareas : abiertas;

  const guardar = async (datos: DatosTarea) => {
    if (modal?.tarea) await api.updateTask(modal.tarea.id, datos);
    else await api.createTask({ ...datos, relatedType, relatedId, relatedName: relatedName || null });
    setModal(null);
    cargar();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider flex items-center gap-2">
          <CheckSquare size={14} />
          {titulo}
          {abiertas.length > 0 && (
            <span className="badge bg-sereno-blue text-sereno-dark normal-case tracking-normal">{abiertas.length}</span>
          )}
        </h3>
        <button onClick={() => setModal({ tarea: null })} className="btn-ghost text-sm flex items-center gap-1.5 !py-1.5">
          <Plus size={15} /> Nueva
        </button>
      </div>

      {cargando ? (
        <LoadingSpinner />
      ) : visibles.length === 0 ? (
        <p className="text-sm text-sereno-dark/40">Sin tareas pendientes</p>
      ) : (
        <div className="space-y-2">
          {visibles.map(t => (
            <FilaTarea
              key={t.id}
              tarea={t}
              compacta
              onCompletar={async tarea => { await api.completeTask(tarea.id); cargar(); }}
              onReabrir={async tarea => { await api.reopenTask(tarea.id); cargar(); }}
              onAplazar={async (tarea, dias) => { await api.postponeTask(tarea.id, dias); cargar(); }}
              onAbrir={tarea => setModal({ tarea })}
            />
          ))}
        </div>
      )}

      {cerradas.length > 0 && (
        <button
          onClick={() => setVerCerradas(v => !v)}
          className="text-xs text-sereno-dark/40 hover:text-sereno-dark mt-3 transition-colors"
        >
          {verCerradas ? 'Ocultar' : `Ver ${cerradas.length} ${cerradas.length === 1 ? 'completada' : 'completadas'}`}
        </button>
      )}

      <TareaModal
        abierto={!!modal}
        tarea={modal?.tarea ?? null}
        relacionFija={{ relatedType, relatedId, relatedName: relatedName || null }}
        onGuardar={guardar}
        onEliminar={modal?.tarea
          ? async () => { await api.deleteTask(modal.tarea!.id); setModal(null); cargar(); }
          : undefined}
        onCerrar={() => setModal(null)}
      />
    </div>
  );
}
